import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage, t } from '../../context/LanguageContext';
import { usePortalAuth } from '../../context/PortalAuthContext';
import podService from '../../services/podService';

const API_URL = process.env.REACT_APP_API_URL;
const portal  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('portalToken')}` } });

// ── Signature Canvas ──────────────────────────────────────────────────────────
function SignatureCanvas({ onSigned }) {
  const canvasRef  = useRef(null);
  const drawing    = useRef(false);
  const lastPoint  = useRef(null);
  const { lang }   = useLanguage();

  const getPoint = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (canvas.width / rect.width), y: (src.clientY - rect.top) * (canvas.height / rect.height) };
  };

  const startDraw = (e) => {
    drawing.current = true;
    lastPoint.current = getPoint(e, canvasRef.current);
    e.preventDefault();
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pt = getPoint(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPoint.current = pt;
  };

  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onSigned(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onSigned(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={560}
        height={180}
        style={{ width: '100%', height: 'auto', border: '1px solid #ced4da', borderRadius: 6, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={clear}>
        {t('Clear', 'Borrar', lang)}
      </button>
    </div>
  );
}

// ── Photo Tab ─────────────────────────────────────────────────────────────────
function PhotoTab({ orderId, onDone, required }) {
  const { lang } = useLanguage();
  const fileRef  = useRef(null);
  const [preview,  setPreview]  = useState(null);
  const [file,     setFile]     = useState(null);
  const [notes,    setNotes]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError('');
  };

  const submit = async () => {
    if (!file) { setError(t('Please take or select a photo first.', 'Por favor tome o seleccione una foto primero.', lang)); return; }
    setSaving(true);
    setError('');
    try {
      const { uploadUrl, key } = await podService.getPortalUploadUrl(orderId, file.type, file.size);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      await podService.savePhotoPod(orderId, key, notes || null);
      onDone('photo');
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to upload photo.', 'Error al subir la foto.', lang));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-muted mb-3" style={{ fontSize: 13 }}>
        {required
          ? t('Required — photograph the package clearly left/handed off at the delivery location (same as Uber Eats-style drop-off proof).', 'Obligatorio — fotografíe el paquete claramente dejado/entregado en el lugar de entrega.', lang)
          : t('Photograph the signed delivery receipt or proof of receipt.', 'Fotografíe el recibo de entrega firmado o prueba de recepción.', lang)}
        {required && <span className="text-danger fw-bold"> *</span>}
      </p>

      {preview ? (
        <div className="mb-3 text-center">
          <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8, border: '1px solid #dee2e6' }} />
          <div className="mt-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => { setPreview(null); setFile(null); fileRef.current.value = ''; }}>
              {t('Retake', 'Retomar', lang)}
            </button>
          </div>
        </div>
      ) : (
        <div className="d-grid mb-3">
          <button
            className="btn btn-outline-primary"
            style={{ padding: '20px', fontSize: 15 }}
            onClick={() => fileRef.current.click()}
          >
            📷 {t('Take / Select Photo', 'Tomar / Seleccionar Foto', lang)}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={onFileChange}
          />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label" style={{ fontSize: 13 }}>{t('Notes (optional)', 'Notas (opcional)', lang)}</label>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={t('Any notes about this delivery…', 'Cualquier nota sobre esta entrega…', lang)}
        />
      </div>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      <div className="d-grid">
        <button className="btn btn-success" onClick={submit} disabled={saving || !file}>
          {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('Uploading…', 'Subiendo…', lang)}</> : t('Submit Photo POD', 'Enviar foto de entrega', lang)}
        </button>
      </div>
    </div>
  );
}

// ── Order summary ─────────────────────────────────────────────────────────────
// Drivers see a packing-slip view (product + qty only) — no prices or order total. Everyone
// else (factory/employee POD capture) keeps the existing full price breakdown.
function OrderSummary({ order, lang, hidePrices }) {
  const items = order?.Items || [];
  if (!items.length) return null;
  const total = items.reduce((sum, it) => sum + (parseFloat(it.unitPriceSnapshot || 0) * (it.quantity || 0)), 0);
  const fmt   = (n) => `$${n.toFixed(2)}`;

  return (
    <div className="card mb-3" style={{ borderColor: '#dee2e6' }}>
      <div className="card-header py-2 fw-semibold" style={{ fontSize: 13, background: '#f8f9fa' }}>
        {hidePrices ? t('Packing Slip', 'Lista de empaque', lang) : t('Items Received', 'Artículos recibidos', lang)}
      </div>
      <div className="card-body p-0">
        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
          <thead style={{ background: '#f8f9fa' }}>
            <tr>
              <th style={{ paddingLeft: 12, fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6' }}>{t('Product', 'Producto', lang)}</th>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6', width: 50 }}>{t('Qty', 'Cant.', lang)}</th>
              {!hidePrices && <th className="text-end" style={{ paddingRight: 12, fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6', width: 80 }}>{t('Price', 'Precio', lang)}</th>}
              {!hidePrices && <th className="text-end" style={{ paddingRight: 12, fontWeight: 600, fontSize: 12, borderBottom: '1px solid #dee2e6', width: 80 }}>{t('Total', 'Total', lang)}</th>}
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td style={{ paddingLeft: 12, verticalAlign: 'middle' }}>{it.Product?.name || '—'}</td>
                <td className="text-center" style={{ verticalAlign: 'middle' }}>{it.quantity}</td>
                {!hidePrices && <td className="text-end" style={{ paddingRight: 12, verticalAlign: 'middle', color: '#6c757d' }}>{fmt(parseFloat(it.unitPriceSnapshot || 0))}</td>}
                {!hidePrices && <td className="text-end" style={{ paddingRight: 12, verticalAlign: 'middle' }}>{fmt(parseFloat(it.unitPriceSnapshot || 0) * (it.quantity || 0))}</td>}
              </tr>
            ))}
          </tbody>
          {!hidePrices && (
            <tfoot>
              <tr style={{ borderTop: '2px solid #dee2e6' }}>
                <td colSpan={3} style={{ paddingLeft: 12, fontWeight: 700, fontSize: 13 }}>
                  {t('Order Total', 'Total del pedido', lang)}
                </td>
                <td className="text-end fw-bold" style={{ paddingRight: 12, fontSize: 14, color: '#198754' }}>
                  {fmt(total)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Signature Tab ─────────────────────────────────────────────────────────────
function SignatureTab({ orderId, onDone, optionalNote }) {
  const { lang }    = useLanguage();
  const [name,      setName]      = useState('');
  const [sigData,   setSigData]   = useState(null);
  const [notes,     setNotes]     = useState('');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  // Pre-fetch GPS as soon as the tab mounts so it's ready by the time user submits
  const gpsRef = useRef({ latitude: null, longitude: null, ready: false });

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        gpsRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, ready: true };
      },
      () => { gpsRef.current.ready = true; }, // denied or unavailable — proceed with nulls
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  const submit = async () => {
    if (!name.trim()) { setError(t('Please enter the recipient\'s name.', 'Por favor ingrese el nombre del destinatario.', lang)); return; }
    if (!sigData)     { setError(t('Please have the customer sign above.', 'Por favor pida al cliente que firme arriba.', lang)); return; }
    setSaving(true);
    setError('');

    // If GPS hasn't resolved yet, give it a short final wait (up to 3 more seconds)
    if (!gpsRef.current.ready) {
      await new Promise(resolve => {
        const deadline = setTimeout(resolve, 3000);
        const check = setInterval(() => {
          if (gpsRef.current.ready) { clearInterval(check); clearTimeout(deadline); resolve(); }
        }, 200);
      });
    }

    const { latitude, longitude } = gpsRef.current;

    try {
      await podService.saveSignaturePod({ orderId, signedByName: name.trim(), signatureDataUrl: sigData, latitude, longitude, receiverNotes: notes || null });
      onDone('signature');
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to save signature.', 'Error al guardar la firma.', lang));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {optionalNote && (
        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
          {t('Optional — only if the customer is present and able to sign.', 'Opcional — solo si el cliente está presente y puede firmar.', lang)}
        </p>
      )}
      <div className="mb-3">
        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
          {t('Recipient Name', 'Nombre del destinatario', lang)} <span className="text-danger">*</span>
        </label>
        <input
          className="form-control form-control-sm"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={t('Full name of person receiving the order', 'Nombre completo de quien recibe el pedido', lang)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
          {t('Signature', 'Firma', lang)} <span className="text-danger">*</span>
        </label>
        <p className="text-muted mb-1" style={{ fontSize: 12 }}>
          {t('Customer signs below with their finger.', 'El cliente firma abajo con su dedo.', lang)}
        </p>
        <SignatureCanvas onSigned={setSigData} />
      </div>

      <div className="mb-3">
        <label className="form-label" style={{ fontSize: 13 }}>{t('Notes (optional)', 'Notas (opcional)', lang)}</label>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={t('Any notes about this delivery…', 'Cualquier nota sobre esta entrega…', lang)}
        />
      </div>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

      <div className="d-grid">
        <button className="btn btn-success" onClick={submit} disabled={saving}>
          {saving
            ? <><span className="spinner-border spinner-border-sm me-2" />{t('Saving…', 'Guardando…', lang)}</>
            : t('Capture & Generate Receipt', 'Capturar y Generar Recibo', lang)}
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PortalPodCapturePage() {
  const { orderId } = useParams();
  const navigate    = useNavigate();
  const { lang }    = useLanguage();
  const { employee } = usePortalAuth();
  // Drivers deliver to a person's home, not a wholesale account — nobody internal should see the
  // customer's prices, and since the customer may not be home, a photo (not a signature) is the
  // only proof that reliably exists, so it's mandatory for drivers specifically. Factory/employee
  // POD (deliveries to businesses, always someone there to sign) keeps its original either-or flow.
  const isDriver    = employee?.staffType === 'driver';
  const [order,     setOrder]   = useState(null);
  const [settings,  setSettings] = useState({ podPhotoEnabled: true, podSignatureEnabled: true });
  const [tab,       setTab]     = useState('photo');
  const [done,      setDone]    = useState(null); // non-driver: 'photo' | 'signature' — either finishes immediately
  const [photoDone,     setPhotoDone]     = useState(false); // driver flow only
  const [signatureDone, setSignatureDone] = useState(false); // driver flow only
  const [loading,   setLoading] = useState(true);
  const [error,     setError]   = useState('');

  const load = useCallback(async () => {
    try {
      const [orders, cfg] = await Promise.all([
        axios.get(`${API_URL}/api/portal/pod/deliveries`, portal()).then(r => r.data),
        axios.get(`${API_URL}/api/pod/settings`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('portalToken')}` } }).catch(() => ({ data: { podPhotoEnabled: true, podSignatureEnabled: true } })).then(r => r.data || r),
      ]);
      const found = orders.find(o => String(o.id) === String(orderId));
      if (!found) { setError(t('Order not found.', 'Pedido no encontrado.', lang)); return; }
      setOrder(found);
      if (cfg) setSettings(cfg);
      // Default to first enabled tab
      if (cfg?.podPhotoEnabled) setTab('photo');
      else if (cfg?.podSignatureEnabled) setTab('signature');
    } catch {
      setError(t('Failed to load order.', 'Error al cargar el pedido.', lang));
    } finally {
      setLoading(false);
    }
  }, [orderId, lang]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-5"><span className="spinner-border spinner-border-sm" /></div>;
  if (error)   return <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>;

  if (done) {
    return (
      <div className="text-center py-5">
        <div style={{ fontSize: 52 }}>{done === 'signature' ? '✅' : '📎'}</div>
        <h5 className="mt-3 fw-bold">
          {done === 'signature'
            ? t('Signature captured!', '¡Firma capturada!', lang)
            : t('Photo submitted!', '¡Foto enviada!', lang)}
        </h5>
        <p className="text-muted" style={{ fontSize: 13 }}>
          {t('Proof of delivery has been recorded.', 'La prueba de entrega ha sido registrada.', lang)}
        </p>
        <button className="btn btn-primary mt-2" onClick={() => navigate('/portal/deliveries')}>
          {t('Back to Deliveries', 'Volver a Entregas', lang)}
        </button>
      </div>
    );
  }

  const showPhoto = settings.podPhotoEnabled !== false;
  const showSig   = settings.podSignatureEnabled !== false;

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => navigate('/portal/deliveries')}>
          ← {t('Deliveries', 'Entregas', lang)}
        </button>
      </div>

      <div className="mb-3">
        <div className="fw-bold" style={{ fontSize: 15 }}>#{order.orderNumber}</div>
        <div className="text-muted" style={{ fontSize: 13 }}>{order.Customer?.name}</div>
      </div>

      <OrderSummary order={order} lang={lang} hidePrices={isDriver} />

      {isDriver && (photoDone || signatureDone) && (
        <div className="alert alert-success py-2" style={{ fontSize: 13 }}>
          {photoDone && <div>✅ {t('Photo saved', 'Foto guardada', lang)}</div>}
          {signatureDone && <div>✅ {t('Signature saved', 'Firma guardada', lang)}</div>}
        </div>
      )}

      {showPhoto && showSig && (
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button className={`nav-link ${tab === 'photo' ? 'active' : ''}`} onClick={() => setTab('photo')}>
              📷 {t('Photo', 'Foto', lang)}{isDriver && !photoDone && <span className="text-danger"> *</span>}{photoDone && ' ✓'}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${tab === 'signature' ? 'active' : ''}`} onClick={() => setTab('signature')}>
              ✍️ {t('Signature', 'Firma', lang)}{signatureDone && ' ✓'}
            </button>
          </li>
        </ul>
      )}

      {(tab === 'photo' && showPhoto && !(isDriver && photoDone)) && (
        <PhotoTab
          orderId={orderId}
          required={isDriver}
          onDone={() => {
            if (!isDriver) { setDone('photo'); return; }
            setPhotoDone(true);
            if (showSig && !signatureDone) setTab('signature');
          }}
        />
      )}
      {(tab === 'signature' && showSig && !(isDriver && signatureDone)) && (
        <SignatureTab orderId={orderId} optionalNote={isDriver} onDone={() => (isDriver ? setSignatureDone(true) : setDone('signature'))} />
      )}

      {isDriver && (
        <div className="mt-3">
          {!photoDone && (
            <p className="text-muted text-center" style={{ fontSize: 12 }}>
              {t('A photo is required before you can finish this delivery.', 'Se requiere una foto antes de poder finalizar esta entrega.', lang)}
            </p>
          )}
          <div className="d-grid">
            <button className="btn btn-primary" disabled={!photoDone} onClick={() => navigate('/portal/deliveries')}>
              {t('Finish Delivery', 'Finalizar Entrega', lang)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
